import * as React from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Link,
  Rating,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { CardBase, GlassTable } from "../../../components/global";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { Talent, User } from "../../../types/users";
import TalentDrawer from "../../talent/TalentDrawer";
import { useInviteTalentMutation } from "../../../services/projectJob.service";
import { Project, ProjectResource } from "../../../types/project";
import { toast } from "react-toastify";
import { useGetFavoriteTalentsQuery } from "../../../services/favoriteTalent.service";
import { useGetTalentsQuery } from "../../../services/talent.service";
import { USStates } from "../../../components/SelectState";

const TableMatched = ({
  project,
  bids,
}: {
  project: Project;
  bids: Array<ProjectResource>;
}) => {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [selectedTalent, setSelectedTalent] = React.useState<Talent | null>(
    null
  );
  const [showTable, setShowTable] = React.useState(true);

  const theme = useTheme();
  const borderColor =
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.08)"
      : "rgba(0, 0, 0, 0.08)";
  const headerGradient =
    theme.palette.mode === "dark"
      ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.22)} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`
      : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.18)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`;
  const rowHoverBg =
    theme.palette.mode === "dark"
      ? alpha(theme.palette.primary.main, 0.16)
      : alpha(theme.palette.primary.main, 0.08);
  const subtleTextColor =
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.7)"
      : "rgba(0, 0, 0, 0.6)";
  const actionButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    transition: "all 0.25s ease",
    boxShadow: "none",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.shadows[4],
    },
    [theme.breakpoints.down("sm")]: {
      px: 1,
      fontSize: "0.75rem",
      py: 0.5,
    },
  } as const;
  const isDark = theme.palette.mode === "dark";
  const headCellSx = {
    fontWeight: 700,
    textTransform: "uppercase",
    fontSize: { xs: "0.6rem", sm: "0.75rem" },
    letterSpacing: { xs: "0.04em", sm: "0.08em" },
    borderBottom: "none",
    color: "#12903C",
    py: { xs: 0.75, sm: 2 },
    px: { xs: 0.5, sm: 2 },
    lineHeight: { xs: 1.2, sm: 1.5 },
    whiteSpace: { xs: "normal", sm: "nowrap" },
    wordBreak: { xs: "break-word", sm: "normal" },
    overflow: { xs: "hidden", sm: "visible" },
    textOverflow: { xs: "ellipsis", sm: "clip" },
  } as const;

  const {
    data: dataTalent,
    isLoading: isLoadingTalent,
    isFetching: isFetchingTalent,
  } = useGetTalentsQuery(
    {
      page: 0,
      pageSize: 100,
      load: "user,user.photo,photo,talent,business_verticals,skills",
      skills: project.skills?.map((it) => it.id),
      state:
        (USStates.find((it) => it.label == project.location?.state)?.value as string) ||
        project.location?.state,
      // city: project.location?.city,
    },
    {
      skip: !showTable,
    }
  );
  const { data: favTalents } = useGetFavoriteTalentsQuery(
    {
      page: 0,
      pageSize: 1000,
    },
    {
      skip: !showTable,
    }
  );

  const rowTalents: Talent[] | undefined = React.useMemo(() => {
    let data = dataTalent?.data;

    const sorted = data?.slice().sort((a: User, b: User) => {
      if (
        a.city === project.location?.city &&
        b.city !== project.location?.city
      ) {
        return -1;
      } else if (
        a.city !== project.location?.city &&
        b.city === project.location?.city
      ) {
        return 1;
      } else {
        // For other cities or if both are project city, maintain original order
        return 0;
      }
    });
    return sorted;
  }, [dataTalent, favTalents, project]);

  const matchedCount = React.useMemo(() => rowTalents?.length ?? 0, [rowTalents]);

  const [doInvite, { isLoading: isInviting }] = useInviteTalentMutation();

  const handleInvite = React.useCallback(
    async (resource_id: number) => {
      try {
        await doInvite({ project_id: project.id, resource_id }).unwrap();
      } catch (err: any) {
        toast.error(err);
      }
    },
    [project]
  );

  return (
    <Box>
      <CardBase
        title="Matched Independent Professionals"
        subtitle={
          matchedCount > 0
            ? `${matchedCount} matched independent professional${matchedCount === 1 ? "" : "s"} found`
            : "No matched independent professionals found"
        }
        useGlassCard={true}
        nested={true}
        headerActions={
          <IconButton
            onClick={() => setShowTable(!showTable)}
            sx={{
              borderRadius: "10px",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: rowHoverBg,
              },
            }}
          >
            {showTable ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        }
        sx={{
          p: 0,
          overflow: "hidden",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 18px 48px rgba(0, 0, 0, 0.5)"
                : "0 18px 48px rgba(0, 0, 0, 0.12)",
          },
        }}
      >
        {showTable && (
          <Box sx={{ px: 0, py: { xs: 0, md: 3 } }}>
            {(isInviting || isFetchingTalent) && (
              <LinearProgress
                sx={{
                  mb: 3,
                  height: 6,
                  borderRadius: "999px",
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  "& .MuiLinearProgress-bar": {
                    borderRadius: "999px",
                  },
                }}
              />
            )}
            <GlassTable hasData={matchedCount > 0}>
              <Table
                stickyHeader
                aria-label="matched-independent-professionals"
                sx={{
                  minWidth: { xs: 300, sm: 650 },
                  "& .MuiTableHead-root .MuiTableRow-root": {
                    background: headerGradient,
                  },
                  "& .MuiTableHead-root .MuiTableCell-root": {
                    ...headCellSx,
                  },
                  "& .MuiTableBody-root .MuiTableCell-root": {
                    borderBottom: `1px solid ${borderColor}`,
                    fontSize: { xs: "0.7rem", sm: "0.95rem" },
                    color: theme.palette.text.primary,
                    py: { xs: 1, sm: 2 },
                    px: { xs: 0.5, sm: 2 },
                    verticalAlign: "top",
                  },
                  "& .MuiTableBody-root .MuiTableRow-root:hover": {
                    backgroundColor: rowHoverBg,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ 
                      maxWidth: { xs: 140, sm: "none" }, 
                      minWidth: { xs: 120, sm: "auto" } 
                    }}>
                      Independent Professional
                    </TableCell>
                    <TableCell align="center" sx={{ 
                      width: { xs: 80, sm: "auto" }, 
                      minWidth: { xs: 70, sm: "auto" } 
                    }}>
                      Phone
                    </TableCell>
                    <TableCell align="center" sx={{ 
                      width: { xs: 120, sm: "auto" }, 
                      minWidth: { xs: 100, sm: "auto" } 
                    }}>
                      Email
                    </TableCell>
                    <TableCell align="center" sx={{ 
                      width: { xs: 80, sm: "auto" }, 
                      minWidth: { xs: 70, sm: "auto" } 
                    }}>
                      Rating
                    </TableCell>
                    <TableCell align="center" sx={{ 
                      width: { xs: 100, sm: "auto" }, 
                      minWidth: { xs: 90, sm: "auto" } 
                    }}>
                      Matched Skills
                    </TableCell>
                    <TableCell align="center" sx={{ 
                      width: { xs: 80, sm: "auto" }, 
                      minWidth: { xs: 70, sm: "auto" } 
                    }}>
                      City
                    </TableCell>
                    <TableCell align="center" sx={{ 
                      minWidth: { xs: 100, sm: "auto" }, 
                      width: { xs: 100, sm: "auto" } 
                    }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {matchedCount === 0 && !isFetchingTalent && (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        sx={{
                          textAlign: "center",
                          py: { xs: 4, sm: 6 },
                          color: subtleTextColor,
                          fontSize: { xs: "0.7rem", sm: "0.95rem" },
                        }}
                      >
                        No matched independent professionals found.
                      </TableCell>
                    </TableRow>
                  )}
                  {rowTalents?.map((talent) => {
                    const matched = project.skills?.filter(
                      (it) => talent.skills.find((s) => s.id == it.id) != undefined
                    );
                    const alreadyBid = !!bids?.find(
                      (it) => it.resource_id == talent.id
                    );
                    const isFavorited = favTalents?.data?.find(
                      (it) => it.user.id == talent?.id
                    ) != undefined;

                    return (
                      <TableRow
                        key={talent.id}
                        sx={{
                          "&:last-child .MuiTableCell-root": {
                            borderBottom: "none",
                          },
                        }}
                      >
                        <TableCell sx={{ width: { xs: 140, md: 260 } }}>
                          <Box>
                            <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }} alignItems="center" flexWrap="wrap">
                              <Link
                                component="button"
                                onClick={() => {
                                  setSelectedTalent(talent);
                                  setDrawerOpen(true);
                                }}
                                sx={{
                                  fontSize: { xs: "0.75rem", sm: "1rem" },
                                  fontWeight: 600,
                                  color: theme.palette.primary.main,
                                  textDecoration: "none",
                                  cursor: "pointer",
                                  "&:hover": {
                                    textDecoration: "underline",
                                  },
                                }}
                              >
                                {talent?.full_name || "N/A"}
                              </Link>
                            </Stack>
                            {isFavorited && (
                              <Chip
                                label="Favorited"
                                size="small"
                                sx={{
                                  mt: 0.25,
                                  fontSize: { xs: "0.65rem", sm: "0.7rem" },
                                  height: { xs: 18, sm: 20 },
                                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                                  color: theme.palette.error.main,
                                }}
                                icon={<Typography sx={{ fontSize: { xs: "0.7rem", sm: "0.8rem" } }}>♥️</Typography>}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center" sx={{ whiteSpace: "nowrap", fontSize: { xs: "0.7rem", sm: "0.95rem" } }}>
                          <Typography variant="body2" sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}>
                            {talent?.phone_number || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.primary.main,
                              textDecoration: "none",
                              fontSize: { xs: "0.7rem", sm: "0.875rem" },
                              "&:hover": {
                                textDecoration: "underline",
                              },
                            }}
                            component="a"
                            href={talent?.email ? `mailto:${talent.email}` : "#"}
                          >
                            {talent?.email || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Rating
                            value={talent?.total_rating ?? 0}
                            readOnly
                            precision={0.5}
                            size="small"
                            sx={{ fontSize: { xs: "0.875rem", sm: "1.25rem" } }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip
                            arrow
                            title={
                              matched && matched.length > 0
                                ? matched.map((it) => it.name).join(", ")
                                : "No matched skills"
                            }
                          >
                            <Chip
                              label={matched?.length || 0}
                              size="small"
                              color={matched && matched.length > 0 ? "primary" : "default"}
                              sx={{
                                fontWeight: 600,
                                borderRadius: "8px",
                                px: 0.75,
                                fontSize: { xs: "0.65rem", sm: "0.75rem" },
                                height: { xs: 20, sm: 24 },
                              }}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center" sx={{ whiteSpace: "nowrap", fontSize: { xs: "0.7rem", sm: "0.95rem" } }}>
                          <Typography variant="body2" sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}>
                            {talent?.city || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ minWidth: { xs: 100, sm: 120 } }}>
                          {alreadyBid ? (
                            <Chip
                              label="Invited"
                              size="small"
                              color="info"
                              sx={{
                                fontWeight: 600,
                                borderRadius: "8px",
                                fontSize: { xs: "0.65rem", sm: "0.75rem" },
                                height: { xs: 20, sm: 24 },
                              }}
                            />
                          ) : (
                            <Button
                              variant="outlined"
                              color="info"
                              sx={actionButtonSx}
                              onClick={() => handleInvite(talent?.id)}
                              disabled={isInviting}
                            >
                              Invite
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </GlassTable>
          </Box>
        )}
      </CardBase>
      <TalentDrawer
        forceRefresh={true}
        data={selectedTalent}
        open={drawerOpen}
        setOpen={setDrawerOpen}
      />
    </Box>
  );
};
export default TableMatched;
